import { CdkPortalOutlet } from '@angular/cdk/portal';
import {
    AfterContentInit,
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ContentChildren,
    ElementRef,
    EventEmitter,
    HostListener,
    inject,
    Input,
    numberAttribute,
    Output,
    QueryList,
    signal,
    ViewChild,
    ViewEncapsulation,
    WritableSignal
} from '@angular/core';
import { startWith } from 'rxjs';
import { OutlineButtonComponent } from '../button/button.component';
import { IconComponent } from '../icon/icon.component';
import { MenuItemComponent } from '../menu/components/item/item.component';
import { MenuTriggerButtonDirective } from '../menu/directives/trigger-button/trigger-button.directive';
import { MenuTriggerDirective } from '../menu/directives/trigger/trigger.directive';
import { MenuComponent } from '../menu/menu.component';
import { TabComponent } from './components/tab/tab.component';

@Component({
    selector: 'nw-tab-group',
    exportAs: 'nwTabGroup',
    templateUrl: './tab-group.component.html',
    styleUrls: ['./tab-group.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        IconComponent,
        CdkPortalOutlet,
        MenuTriggerDirective,
        MenuItemComponent,
        MenuComponent,
        OutlineButtonComponent,
        MenuTriggerButtonDirective
    ]
})
export class TabGroupComponent implements AfterContentInit, AfterViewInit {
    @Input({ transform: numberAttribute }) selectedIndex!: number;

    @Output() readonly selectedIndexChange: EventEmitter<number> =
        new EventEmitter<number>();

    @ContentChildren(TabComponent) tabs!: QueryList<TabComponent>;
    @ViewChild('tabGroup') tabGroup!: ElementRef;

    public tabs$$: WritableSignal<TabComponent[]> = signal([]);
    public isOverflow$$: WritableSignal<boolean> = signal(false);

    @HostListener('window:resize') resize(): void {
        this.detectOverflow();
    }

    private elementRef: ElementRef = inject(ElementRef);

    public ngAfterContentInit(): void {
        this.tabs.changes
            .pipe(startWith(this.tabs))
            .subscribe((tabs: QueryList<TabComponent>) => {
                this.tabs$$.set(tabs.toArray());
                this.waitForElementsAndDetectOverflow();
            });

        this.selectTab(this.selectedIndex, false);
    }

    public ngAfterViewInit(): void {
        this.waitForElementsAndDetectOverflow();
    }

    public handleClick(tab: TabComponent, indexToSelect: number): void {
        if (tab.disabled) return;

        this.selectTab(indexToSelect);
    }

    private selectTab(selectedIndex: number, emit: boolean = true): void {
        this.selectedIndex = selectedIndex || 0;
        this.tabs.forEach((tab, index) => (tab.active = index === this.selectedIndex));
        if (emit) this.selectedIndexChange.emit(this.selectedIndex);
    }

    private detectOverflow(): void {
        const ELEMENT: HTMLElement = this.elementRef?.nativeElement as HTMLElement;

        if (!ELEMENT || !this.tabGroup) return;

        const OVERFLOW: boolean =
            (this.tabGroup?.nativeElement.clientWidth || ELEMENT.scrollWidth || 0) >
            (ELEMENT.clientWidth || 0);

        if (OVERFLOW !== this.isOverflow$$()) this.isOverflow$$.set(OVERFLOW);
    }

    private waitForElementsAndDetectOverflow(): void {
        if (this.elementRef?.nativeElement && this.tabGroup?.nativeElement) {
            this.detectOverflow();
            return;
        }

        // webkit seems to call this a bit prematurely
        // so lets just wait for the elements to ready and recheck overflow
        function waitForElementsOrTimeout(
            elementRef: HTMLElement,
            tabGroup: HTMLElement,
            timeoutMs: number,
            callback: (result: { timedOut: boolean }) => void
        ) {
            const checkVars = () => {
                if (elementRef !== undefined && tabGroup !== undefined) {
                    clearInterval(checkInterval);
                    clearTimeout(timeout);
                    callback({ timedOut: false });
                }
            };

            const checkInterval = setInterval(checkVars, 100);

            const timeout = setTimeout(() => {
                clearInterval(checkInterval);
                callback({ timedOut: true });
            }, timeoutMs);
        }

        waitForElementsOrTimeout(
            this.elementRef?.nativeElement,
            this.tabGroup?.nativeElement,
            10000,
            (result) => {
                if (result.timedOut) return;

                this.detectOverflow();
            }
        );
    }
}
